import moment from "moment";

export type RangePreset = "last_12_months" | "last_24_months" | "year_to_date" | "last_year" | "all_time" | "custom";
export type Currency = "cad" | "usd";
export type Payments = "online" | "offline" | "all";

export interface IMetricsFilters {
  preset: RangePreset;
  customFrom: string;
  customTo: string;
  currency: Currency;
  payments: Payments;
}

export const RANGE_OPTIONS: { id: RangePreset; value: string }[] = [
  { id: "last_12_months", value: "Last 12 months" },
  { id: "last_24_months", value: "Last 24 months" },
  { id: "year_to_date", value: "Year to date" },
  { id: "last_year", value: "Last year" },
  { id: "all_time", value: "All time" },
  { id: "custom", value: "Custom" },
];

export const CURRENCY_OPTIONS: { id: Currency; value: string }[] = [
  { id: "cad", value: "CAD" },
  { id: "usd", value: "USD" },
];

export const PAYMENTS_OPTIONS: { id: Payments; value: string }[] = [
  { id: "online", value: "Online (card + bank)" },
  { id: "offline", value: "Offline (cash, cheque, other)" },
  { id: "all", value: "All payments" },
];

// EagerDog's first events were in 2023
const ALL_TIME_START = "2023-01-01";

export const resolveRange = (filters: IMetricsFilters): { from: string; to: string } => {
  const now = moment();
  switch (filters.preset) {
    case "last_24_months":
      return { from: now.clone().subtract(23, "months").startOf("month").toISOString(), to: now.toISOString() };
    case "year_to_date":
      return { from: now.clone().startOf("year").toISOString(), to: now.toISOString() };
    case "last_year":
      return { from: now.clone().subtract(1, "year").startOf("year").toISOString(), to: now.clone().startOf("year").toISOString() };
    case "all_time":
      return { from: moment(ALL_TIME_START).toISOString(), to: now.toISOString() };
    case "custom":
      return {
        from: moment(filters.customFrom || ALL_TIME_START).startOf("day").toISOString(),
        to: moment(filters.customTo || undefined).endOf("day").toISOString(),
      };
    default:
      return { from: now.clone().subtract(11, "months").startOf("month").toISOString(), to: now.toISOString() };
  }
};

export const formatMonth = (month: string) => moment(month, "YYYY-MM").format("MMM YY");

export const formatNumber = (n: number) => new Intl.NumberFormat("en-US").format(Math.round(n || 0));

export const formatMoney = (n: number, currency: Currency, compact = false) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
    maximumFractionDigits: compact ? 0 : 2,
    notation: compact && Math.abs(n) >= 10000 ? "compact" : "standard",
  }).format(n || 0);

export const formatPercent = (n: number) => `${((n || 0) * 100).toFixed(1)}%`;

export const percentChange = (current: number, previous: number): number | undefined =>
  previous > 0 ? (current - previous) / previous : undefined;

export const downloadCsv = (fileName: string, rows: Record<string, unknown>[]) => {
  if (!rows.length) return;
  const headers = Object.keys(rows[0]);
  const escape = (v: unknown) => {
    const s = v === null || v === undefined ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [headers.join(","), ...rows.map((r) => headers.map((h) => escape(r[h])).join(","))].join("\n");
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = `${fileName}.csv`;
  link.click();
  URL.revokeObjectURL(url);
};

// Keep in sync with the brand palette in styles/variables.scss
export const CHART_COLORS = ["#F25A2D", "#7887EB", "#6FC596", "#FFBF70", "#FF875C", "#B7261F", "#939393", "#FFAD4A", "#333333"];
export const CHART_TEXT = "#666666";
export const CHART_LINE = "#333333";

export const AXIS_PROPS = {
  tick: { fontSize: 12, fill: CHART_TEXT, fontFamily: "Roboto" },
  axisLine: { stroke: "#DDDDDD" },
  tickLine: false,
};

export const TOOLTIP_PROPS = {
  contentStyle: { fontFamily: "Roboto", fontSize: 13, borderRadius: 4, border: "1px solid #DDDDDD", color: "#333333" },
  labelStyle: { fontWeight: 500 },
};

export const LEGEND_PROPS = {
  wrapperStyle: { fontFamily: "Roboto", fontSize: 13, color: CHART_TEXT },
};

// Keeps the top N rows and folds the rest into "Other"
export const topWithOther = <T extends { key: string; count: number }>(rows: T[], limit: number) => {
  if (rows.length <= limit) return rows.map(({ key, count }) => ({ key, count }));
  const top = rows.slice(0, limit).map(({ key, count }) => ({ key, count }));
  const other = rows.slice(limit).reduce((n, r) => n + r.count, 0);
  return [...top, { key: "Other", count: other }];
};
